import type {
  AgentRuntimeActionOutcome,
  AgentRuntimeMessageAttachment,
  AgentRuntimePreferredMcpServerRef,
  AgentRuntimeRequestedSkillRef,
  AgentRuntimeScratchPadRef,
  AgentRuntimeSessionContextRef,
} from "@cord/shared"
import type {
  LocalOutboxQueueRecord,
  PendingQueueRecord,
} from "@/lib/session-queue-ui-state"
import type {
  QueuedSessionCommandOutcomeNotice,
  SessionActionOutcomeNotice,
} from "@/hooks/thread-sync-client-types"
import { normalizeAttachedPullRequest } from "@/lib/attached-pull-request"
import {
  normalizePreferredMcpServerRefs,
  normalizeRequestedSkillRefs,
} from "@/lib/message-link-references"
import { normalizeSessionContextSummaryIntent } from "@/lib/session-context-refs"

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function asNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null
  return Math.floor(value)
}

function asBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null
}

function normalizeMessageAttachments(value: unknown): AgentRuntimeMessageAttachment[] | undefined {
  if (!Array.isArray(value)) return undefined
  const attachments: AgentRuntimeMessageAttachment[] = []
  for (const item of value) {
    const row = asRecord(item)
    const id = asString(row?.id)
    const url = asString(row?.url)
    if (!id || !url) continue
    attachments.push({
      id,
      url,
      filename: asString(row?.filename) ?? undefined,
      mime: asString(row?.mime) ?? undefined,
    })
  }
  return attachments.length > 0 ? attachments : undefined
}

function normalizeContextRefs(value: unknown): AgentRuntimeSessionContextRef[] | undefined {
  if (!Array.isArray(value)) return undefined
  const refs: AgentRuntimeSessionContextRef[] = []
  for (const item of value) {
    const row = asRecord(item)
    const sessionId = asString(row?.sessionId)
    if (!sessionId) continue
    refs.push({
      sessionId,
      localId: asString(row?.localId) ?? undefined,
      referenceId: asString(row?.referenceId) ?? undefined,
      repositoryId: asString(row?.repositoryId),
      title: asString(row?.title),
      origin:
        row?.origin === "context" ||
        row?.origin === "continue" ||
        row?.origin === "continue_new_session" ||
        row?.origin === "pr_linked_session"
          ? row.origin
          : undefined,
      summaryIntent: normalizeSessionContextSummaryIntent(row?.summaryIntent) === "code_review"
        ? "code_review"
        : undefined,
    })
  }
  return refs.length > 0 ? refs : undefined
}

function normalizeRequestedSkillRefsState(
  value: unknown,
): AgentRuntimeRequestedSkillRef[] | undefined {
  const refs = normalizeRequestedSkillRefs(value)
  return refs.length > 0 ? refs : undefined
}

function normalizePreferredMcpServerRefsState(
  value: unknown,
): AgentRuntimePreferredMcpServerRef[] | undefined {
  const refs = normalizePreferredMcpServerRefs(value)
  return refs.length > 0 ? refs : undefined
}

function normalizeScratchPadRefsState(
  value: unknown,
): AgentRuntimeScratchPadRef[] | undefined {
  if (!Array.isArray(value)) return undefined
  const refs: AgentRuntimeScratchPadRef[] = []
  for (const item of value) {
    const row = asRecord(item)
    const sessionId = asString(row?.sessionId)
    const label = asString(row?.label)
    const content = typeof row?.content === "string" ? row.content : null
    if (!sessionId || !label || content === null) continue
    refs.push({ sessionId, label, content })
  }
  return refs.length > 0 ? refs : undefined
}

export function normalizePendingQueueRecord(value: unknown): PendingQueueRecord | null {
  const row = asRecord(value)
  const text = typeof row?.text === "string" ? row.text : ""
  const timestamp = asNumber(row?.timestamp)
  if (timestamp === null) return null
  const status =
    row?.status === "dispatching" || row?.status === "submit_uncertain" || row?.status === "accepted"
      ? row.status
      : "queued"
  return {
    id: asNumber(row?.id) ?? asString(row?.id) ?? `pending:${timestamp}`,
    clientMessageId: asString(row?.clientMessageId) ?? undefined,
    clientActionId: asString(row?.clientActionId) ?? undefined,
    conversationId: asString(row?.conversationId) ?? "default",
    text,
    attachmentCount: asNumber(row?.attachmentCount) ?? undefined,
    attachments: normalizeMessageAttachments(row?.attachments),
    contextRefs: normalizeContextRefs(row?.contextRefs),
    requestedSkillRefs: normalizeRequestedSkillRefsState(row?.requestedSkillRefs),
    preferredMcpServerRefs: normalizePreferredMcpServerRefsState(row?.preferredMcpServerRefs),
    scratchPadRefs: normalizeScratchPadRefsState(row?.scratchPadRefs),
    attachedPullRequest: normalizeAttachedPullRequest(row?.attachedPullRequest),
    actor: asRecord(row?.actor) ?? undefined,
    timestamp,
    status,
    source: "server",
    queueKind: row?.queueKind === "session_action" ? "session_action" : "message",
    serverObserved: true,
  }
}

export function normalizeQueuedMessageDraft(input: {
  clientMessageId: string
  message: { type: string; [key: string]: unknown }
  nowMs?: number
}): LocalOutboxQueueRecord {
  const previewUrls = Array.isArray(input.message.previewUrls)
    ? input.message.previewUrls.filter((value): value is string => typeof value === "string")
    : []

  // Build proper attachment objects so queued-message UI can show inline
  // image previews instead of a plain "N attachment(s)" fallback.
  const metaArray = Array.isArray(input.message.previewAttachmentsMeta)
    ? (input.message.previewAttachmentsMeta as Array<{ filename?: string; mime?: string }>)
    : []
  const attachments: AgentRuntimeMessageAttachment[] | undefined =
    previewUrls.length > 0
      ? previewUrls.map((url, index) => ({
          id: `preview-${input.clientMessageId}-${index}`,
          url,
          filename: asString(metaArray[index]?.filename) ?? undefined,
          mime: asString(metaArray[index]?.mime) ?? undefined,
        }))
      : undefined

  return {
    clientMessageId: input.clientMessageId,
    conversationId: asString(input.message.conversationId) ?? "default",
    text: typeof input.message.text === "string" ? input.message.text : "",
    attachmentCount: previewUrls.length,
    attachments,
    contextRefs: normalizeContextRefs(input.message.contextRefs),
    requestedSkillRefs: normalizeRequestedSkillRefsState(input.message.requestedSkillRefs),
    preferredMcpServerRefs: normalizePreferredMcpServerRefsState(input.message.preferredMcpServerRefs),
    scratchPadRefs: normalizeScratchPadRefsState(input.message.scratchPadRefs),
    attachedPullRequest: normalizeAttachedPullRequest(input.message.attachedPullRequest),
    timestamp: input.nowMs ?? Date.now(),
    observedServerStatus: null,
  }
}

export function normalizeRuntimeActionOutcome(value: unknown): AgentRuntimeActionOutcome | null {
  const row = asRecord(value)
  const action = asString(row?.action)
  const requestKind = asString(row?.requestKind)
  const requestId = asString(row?.requestId)
  const outcome = asString(row?.outcome)
  const code = asString(row?.code)
  const message = asString(row?.message)
  if (!action || !requestKind || !requestId || !outcome || !code || !message) {
    return null
  }
  if (outcome !== "noop" && outcome !== "conflict" && outcome !== "invalid") {
    return null
  }
  return {
    action,
    requestKind,
    requestId,
    outcome,
    code,
    message,
    conversationId: asString(row?.conversationId) ?? undefined,
    targetUserId: asString(row?.targetUserId) ?? undefined,
    winningAction: asString(row?.winningAction) ?? undefined,
    resolvedByUserId: asString(row?.resolvedByUserId) ?? undefined,
    resolvedByLabel: asString(row?.resolvedByLabel) ?? undefined,
    timestamp: asNumber(row?.timestamp) ?? undefined,
  }
}

export function normalizeSessionActionNotice(
  value: unknown,
  nowMs: number = Date.now(),
): SessionActionOutcomeNotice | null {
  const outcome = normalizeRuntimeActionOutcome(value)
  if (!outcome) return null
  return {
    id: `${outcome.requestKind}:${outcome.requestId}:${outcome.timestamp ?? nowMs}`,
    requestId: outcome.requestId,
    requestKind: outcome.requestKind,
    outcome: outcome.outcome,
    message: outcome.message,
    code: outcome.code,
    action: outcome.action,
    conversationId: outcome.conversationId,
    targetUserId: outcome.targetUserId,
    timestamp: outcome.timestamp,
  }
}

export function normalizeQueuedSessionCommandNotice(
  value: unknown,
  nowMs: number = Date.now(),
): QueuedSessionCommandOutcomeNotice | null {
  const row = asRecord(value)
  const clientActionId = asString(row?.clientActionId)
  const outcome = asString(row?.outcome)
  const message = asString(row?.message)
  const code = asString(row?.code)
  if (!clientActionId || !message || !code) {
    return null
  }
  if (outcome !== "rejected" && outcome !== "submit_uncertain") {
    return null
  }
  const timestamp = asNumber(row?.timestamp) ?? nowMs
  return {
    id: `${clientActionId}:${outcome}:${timestamp}`,
    clientActionId,
    outcome,
    message,
    code,
    timestamp,
  }
}

export function normalizeRejectionRecoveryFlag(value: unknown): boolean {
  return asBoolean(value) === true
}
